import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { NavigationItem } from '../types';
import LanguageSwitcher from './LanguageSwitcher';

export default function Navigation() {
  const { t } = useTranslation('navigation');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems: NavigationItem[] = [
    { id: 'home', label: t('menu.home'), href: '#home' },
    { id: 'news', label: t('menu.news'), href: '#news' },
    { id: 'about', label: t('menu.about'), href: '#about' },
    { id: 'contact', label: t('menu.contact'), href: '#contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'nav-scrolled' : 'bg-black/60 backdrop-blur-sm'
      }`}
    >
      <div className='max-w-7xl mx-auto px-6 py-4'>
        <div className='flex items-center justify-between'>
          {/* Logo */}
          <div className='flex items-center gap-2'>
            <a
              href='#home'
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('#home');
              }}
              className='flex items-center'
            >
              <img
                src='/assets/logo.webp'
                alt={t('logo.alt')}
                className='h-12 md:h-16 w-auto'
                style={{
                  filter:
                    'brightness(0) invert(1) sepia(1) saturate(5) hue-rotate(-10deg) brightness(0.95) drop-shadow(0 0 8px rgba(250, 204, 21, 0.3))',
                }}
              />
            </a>
            <div className='text-white text-sm'>
              <div>{t('logo.companyNameEn')}</div>
              <div>{t('logo.companyNameZh')}</div>
            </div>
          </div>

          {/* Desktop Navigation Menu + Language Switcher */}
          <div className='hidden md:flex items-center space-x-6'>
            {navigationItems.map((item) => (
              <a
                key={item.id}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(item.href);
                }}
                className='text-white font-medium hover:text-yellow-400 transition-colors'
              >
                {item.label}
              </a>
            ))}
            <LanguageSwitcher />
          </div>

          {/* Mobile Menu Button */}
          <button
            className='md:hidden text-yellow-400'
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={t('aria.toggleMobileMenu', {
              ns: 'common',
            })}
          >
            <svg
              className='w-6 h-6'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M6 18L18 6M6 6l12 12'
                />
              ) : (
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M4 6h16M4 12h16M4 18h16'
                />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className='md:hidden mt-4 pb-4 space-y-3'>
            {navigationItems.map((item) => (
              <a
                key={item.id}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(item.href);
                }}
                className='block text-white font-medium hover:text-yellow-400 transition-colors py-2'
              >
                {item.label}
              </a>
            ))}
            <LanguageSwitcher isMobile />
          </div>
        )}
      </div>
    </nav>
  );
}
